import React, { useState, useEffect, useRef } from "react";
import { Input, Checkbox, Button, Spin } from "antd";
import { SearchOutlined, DownOutlined } from "@ant-design/icons";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../CustomWidgets/Laoding";
import { detectWebDeviceType } from "../../utils";

const makeTempId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const emptySubgroup = () => ({
  id: makeTempId(),
  englishName: "",
  arabicName: "",
  selectedProducts: [],
  searchQuery: "",
  products: [],
  loading: false,
  showDropdown: false,
});

const NewGroup_Modal = ({ open, onClose, onSave, groupLoading, initialData }) => {
  const { t, i18n } = useTranslation();
  const deviceType = detectWebDeviceType();

  const [englishName, setEnglishName] = useState("");
  const [arabicName, setArabicName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const userData = useSelector((state) => state?.login?.user?.result);

  const [subgroupSelected, setSubgroupSelected] = useState(false);
  const [subgroups, setSubgroups] = useState([emptySubgroup()]);
  const subgroupDropdownRefs = useRef({});
  const subgroupTimers = useRef({});

  const [assignToParent, setAssignToParent] = useState(false);
  const [parentGroup, setParentGroup] = useState(null);
  const [parentSearchQuery, setParentSearchQuery] = useState("");
  const [parentGroups, setParentGroups] = useState([]);
  const [parentLoading, setParentLoading] = useState(false);
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const parentDropdownRef = useRef(null);
  const parentToggleRef = useRef(null);
  const parentTimer = useRef(null);

  const isEditing = Boolean(initialData?.id);

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      setEnglishName(initialData.name || "");
      setArabicName(initialData.name_ar || "");
      setSelectedProducts(initialData.product_ids || []);

      if (initialData.sub_group_list?.length) {
        setSubgroupSelected(true);
        setSubgroups(
          initialData.sub_group_list.map((sg) => ({
            id: sg.id,
            englishName: sg.name || "",
            arabicName: sg.name_ar || "",
            selectedProducts: sg.product_ids || [],
            searchQuery: "",
            products: [],
            loading: false,
            showDropdown: false,
          }))
        );
      } else {
        setSubgroupSelected(false);
        setSubgroups([emptySubgroup()]);
      }

      if (initialData.parent_id) {
        setAssignToParent(true);
        setParentGroup({
          id: initialData.parent_id,
          name: initialData.parent_name,
          name_ar: initialData.parent_name_ar,
        });
      } else {
        setAssignToParent(false);
        setParentGroup(null);
      }
    }
  }, [open, initialData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        toggleBtnRef.current &&
        !toggleBtnRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }

      if (
        parentDropdownRef.current &&
        !parentDropdownRef.current.contains(event.target) &&
        parentToggleRef.current &&
        !parentToggleRef.current.contains(event.target)
      ) {
        setShowParentDropdown(false);
      }

      Object.entries(subgroupDropdownRefs.current).forEach(([subId, refs]) => {
        if (
          refs?.dropdown &&
          !refs.dropdown.contains(event.target) &&
          refs?.toggle &&
          !refs.toggle.contains(event.target)
        ) {
          setSubgroups((prev) =>
            prev.map((sg) => (sg.id === subId ? { ...sg, showDropdown: false } : sg))
          );
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getProducts = async (val = "") => {
    setLoading(true);
    try {
      const payload = {
        company_id: userData?.company_id,
        product_name: val,
        barcode: "",
        limit: 10,
        offset: 0,
        lang: i18n.language === "ar" ? "ar_001" : "en_US",
      };
      const res = await axiosInstance.post(URLS.GET_PRODUCT_SEARCH, payload);
      const result = res?.data?.result?.[0]?.response || [];
      setProducts(result);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      if (searchQuery.length === 0 || searchQuery.length > 2) {
        getProducts(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleProduct = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const getParentGroups = async (val = "") => {
    setParentLoading(true);
    try {
      const payload = {
        user_id: userData?.user_id,
        company_id: userData?.company_id,
        search: val,
      };
      const res = await axiosInstance.post(URLS.GET_PARENT_PRODUCT_GROUP, payload);
      const result = res?.data?.product_groups || res?.data?.result || [];
      setParentGroups(result);
    } catch (err) {
      console.error("Error fetching parent groups:", err);
      setParentGroups([]);
    } finally {
      setParentLoading(false);
    }
  };

  useEffect(() => {
    if (open && assignToParent) getParentGroups();
  }, [open, assignToParent]);

  const handleParentSearchChange = (val) => {
    setParentSearchQuery(val);
    clearTimeout(parentTimer.current);
    parentTimer.current = setTimeout(() => {
      if (val.length === 0 || val.length > 2) getParentGroups(val);
    }, 300);
  };

  const handleAssignToParentToggle = (checked) => {
    setAssignToParent(checked);
    if (checked) {
      setSubgroupSelected(false);
    } else {
      setParentGroup(null);
      setParentSearchQuery("");
    }
  };

  const handleSubgroupToggle = (checked) => {
    setSubgroupSelected(checked);
    if (checked) {
      setSubgroups((prev) => (prev.length ? prev : [emptySubgroup()]));
      setSelectedProducts([]);
      setAssignToParent(false);
      setParentGroup(null);
    } else {
      setSubgroups([]);
    }
  };

  const addSubgroup = () => {
    setSubgroups((prev) => [...prev, emptySubgroup()]);
  };

  const removeSubgroup = (subId) => {
    setSubgroups((prev) => prev.filter((sg) => sg.id !== subId));
    delete subgroupDropdownRefs.current[subId];
    clearTimeout(subgroupTimers.current[subId]);
    delete subgroupTimers.current[subId];
  };

  const updateSubgroupField = (subId, field, value) => {
    setSubgroups((prev) =>
      prev.map((sg) => (sg.id === subId ? { ...sg, [field]: value } : sg))
    );
  };

  const toggleSubgroupProduct = (subId, prodId) => {
    setSubgroups((prev) =>
      prev.map((sg) => {
        if (sg.id !== subId) return sg;
        const already = sg.selectedProducts.includes(prodId);
        return {
          ...sg,
          selectedProducts: already
            ? sg.selectedProducts.filter((id) => id !== prodId)
            : [...sg.selectedProducts, prodId],
        };
      })
    );
  };

  const fetchSubgroupProducts = async (subId, val = "") => {
    updateSubgroupField(subId, "loading", true);
    try {
      const payload = {
        company_id: userData?.company_id,
        product_name: val,
        barcode: "",
        limit: 10,
        offset: 0,
        lang: i18n.language === "ar" ? "ar_001" : "en_US",
      };
      const res = await axiosInstance.post(URLS.GET_PRODUCT_SEARCH, payload);
      const result = res?.data?.result?.[0]?.response || [];
      updateSubgroupField(subId, "products", result);
    } catch (err) {
      console.error("Error fetching subgroup products:", err);
      updateSubgroupField(subId, "products", []);
    } finally {
      updateSubgroupField(subId, "loading", false);
    }
  };

  const handleSubgroupSearchChange = (subId, val) => {
    updateSubgroupField(subId, "searchQuery", val);
    clearTimeout(subgroupTimers.current[subId]);
    subgroupTimers.current[subId] = setTimeout(() => {
      if (val.length === 0 || val.length > 2) fetchSubgroupProducts(subId, val);
    }, 300);
  };

  const openSubgroupDropdown = (subId) => {
    setSubgroups((prev) =>
      prev.map((sg) => ({ ...sg, showDropdown: sg.id === subId ? !sg.showDropdown : sg.showDropdown }))
    );
  };

  const handleClose = () => {
    setEnglishName("");
    setArabicName("");
    setSearchQuery("");
    setSelectedProducts([]);
    setShowDropdown(false);
    setSubgroupSelected(false);
    setSubgroups([emptySubgroup()]);
    setAssignToParent(false);
    setParentGroup(null);
    setParentSearchQuery("");
    setShowParentDropdown(false);
    onClose?.();
  };

  const handleSave = () => {
    if (englishName.trim() === "" || arabicName.trim() === "") {
      toast.error(t("Please enter both English and Arabic group names."));
      return;
    }

    if (subgroupSelected) {
      const incomplete = subgroups.some(
        (sg) => sg.englishName.trim() === "" || sg.arabicName.trim() === ""
      );
      if (incomplete) {
        toast.error(t("Please enter both English and Arabic names for every subgroup."));
        return;
      }
    }

    if (assignToParent && !parentGroup) {
      toast.error(t("Please select a parent group."));
      return;
    }

    const payload = {
      user_id: userData?.user_id,
      company_id: userData?.company_id,
      name: englishName,
      name_ar: arabicName,
    };

    if (isEditing) payload.id = initialData.id;

    if (subgroupSelected && subgroups.length > 0) {
      payload.is_create_sub_group = true;
      payload.sub_groups_vals = subgroups.map((sg) => {
        const subVal = {
          name: sg.englishName,
          name_ar: sg.arabicName,
          product_ids: sg.selectedProducts,
        };
        if (!String(sg.id).startsWith("tmp_")) subVal.id = sg.id;
        return subVal;
      });
    } else {
      payload.product_ids = selectedProducts;
    }

    if (assignToParent && parentGroup) {
      payload.parent_id = parentGroup.id;
    } else if (isEditing && initialData?.parent_id && !assignToParent) {
      payload.parent_id = false;
      payload.remove_parent_id = initialData.parent_id;
    }

    onSave?.(payload);
  };

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex justify-center ${deviceType == "tablet" ? "pt-32" : "items-center"
        }`}
      style={{ backdropFilter: "blur(10px)", backgroundColor: "rgba(0,0,0,0.4)" }}
    >
      {groupLoading && <LoadingSpinner />}

      <div
        className={`relative bg-white rounded-[20px] p-10 h-fit shadow-2xl animate-fadeIn overflow-y-auto ${deviceType == "tablet" ? "w-[1200px]" : "w-[900px]"
          }`}
        style={{ maxHeight: "90vh" }}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-black text-2xl font-semibold"
        >
          ×
        </button>

        <h2 className="text-3xl font-bold mb-6">
          {isEditing ? t("Edit Group") : t("Create New Group")}
        </h2>

        <div className={`${deviceType == "tablet" ? "flex justify-between gap-x-6 items-end" : ""}`}>
          <div className={`mb-8 ${deviceType == "tablet" ? "w-1/2" : ""}`}>
            <h3 className="text-xl font-semibold mb-4 text-gray-600">{t("Group Name")}</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-lg mb-2 font-medium text-gray-600">{t("English")}</label>
                <Input
                  size="large"
                  placeholder={t("Enter group name")}
                  value={englishName}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\u0600-\u06FFa-zA-Z0-9\s]/g, "");
                    setEnglishName(val.trimStart());
                  }}
                  maxLength={25}
                  onBlur={() => setEnglishName((prev) => prev.trim())}
                  className="rounded-md h-12 text-lg"
                />
              </div>
              <div>
                <label className="block text-lg mb-2 font-medium text-gray-600">{t("Arabic")}</label>
                <Input
                  size="large"
                  placeholder="أدخل اسم المجموعة"
                  value={arabicName}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\u0600-\u06FFa-zA-Z0-9\s]/g, "");
                    setArabicName(val.trimStart());
                  }}
                  maxLength={25}
                  onBlur={() => setArabicName((prev) => prev.trim())}
                  dir="rtl"
                  className="rounded-md h-12 text-lg"
                />
              </div>
            </div>
          </div>

          <div className="flex  gap-x-10 mb-5">
            <Checkbox
              className="custom-checkbox scale-90"
              checked={subgroupSelected}
              onChange={(e) => {
                e.stopPropagation();
                handleSubgroupToggle(e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              disabled={assignToParent}
            >
              <span style={{ fontWeight: "bold", fontSize: "20px" }}>
                {t("Create Subgroups (Optional)")}
              </span>
            </Checkbox>
          </div>
        </div>

        {/* Parent group picker */}
        {assignToParent && (
          <div className="mb-8 relative w-1/2">
            <label className="block text-lg mb-2 font-medium text-gray-600">
              {t("Parent Group")}
            </label>
            <button
              ref={parentToggleRef}
              onClick={() => setShowParentDropdown((v) => !v)}
              className="w-full flex items-center justify-between border border-gray-300 rounded-md h-12 px-4 text-lg focus:outline-none"
            >
              <span>{parentGroup ? parentGroup.name : t("Select parent group")}</span>
              <DownOutlined className={`transition-transform ${showParentDropdown ? "rotate-180" : ""}`} />
            </button>

            {showParentDropdown && (
              <div
                ref={parentDropdownRef}
                className="absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg w-full mt-2 overflow-y-auto p-4 custom-scrollbar"
                style={{ maxHeight: "300px" }}
              >
                <Input
                  prefix={<SearchOutlined />}
                  placeholder={t("Search parent groups...")}
                  value={parentSearchQuery}
                  onChange={(e) => handleParentSearchChange(e.target.value)}
                  className="mb-3 text-lg h-12 rounded-md focus:outline-none"
                />
                {parentLoading ? (
                  <div className="flex justify-center text-lg py-4">
                    <Spin />
                  </div>
                ) : parentGroups.length > 0 ? (
                  parentGroups.map((pg) => (
                    <div
                      key={pg.id}
                      onClick={() => {
                        setParentGroup(pg);
                        setShowParentDropdown(false);
                      }}
                      className={`flex items-center py-2 px-2 rounded-md cursor-pointer hover:bg-green-50 ${parentGroup?.id === pg.id ? "bg-green-50" : ""
                        }`}
                    >
                      <span className="text-gray-800 text-lg">{pg.name}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-400 py-4">{t("No groups found")}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Subgroups list — enlarged: bigger cards, headings, inputs & controls */}
        {subgroupSelected && (
          <div className="mt-2 mb-8 space-y-8">
            {subgroups.map((subgroup, index) => (
              <div key={subgroup.id} className="border-2 rounded-2xl p-8 bg-gray-50 space-y-6 relative">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-2xl">{t("Subgroup")} {index + 1}</h3>
                  {subgroups.length > 1 && (
                    <Button danger size="large" onClick={() => removeSubgroup(subgroup.id)}>
                      {t("Remove")}
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-lg mb-2 font-medium text-gray-600">{t("English Name")}</label>
                    <Input
                      size="large"
                      placeholder={t("English Name")}
                      value={subgroup.englishName}
                      maxLength={25}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\u0600-\u06FFa-zA-Z0-9\s]/g, "");
                        updateSubgroupField(subgroup.id, "englishName", val.trimStart());
                      }}
                      onBlur={() =>
                        updateSubgroupField(subgroup.id, "englishName", subgroup.englishName.trim())
                      }
                      className="rounded-md h-12 text-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-lg mb-2 font-medium text-gray-600">{t("Arabic Name")}</label>
                    <Input
                      size="large"
                      placeholder={t("Arabic Name")}
                      dir="rtl"
                      value={subgroup.arabicName}
                      maxLength={25}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\u0600-\u06FFa-zA-Z0-9\s]/g, "");
                        updateSubgroupField(subgroup.id, "arabicName", val.trimStart());
                      }}
                      onBlur={() =>
                        updateSubgroupField(subgroup.id, "arabicName", subgroup.arabicName.trim())
                      }
                      className="rounded-md h-12 text-lg"
                    />
                  </div>
                </div>

                {/* Per-subgroup product picker */}
                <div className="relative">
                  <label className="block text-lg mb-2 font-medium text-gray-600">
                    {t("Product List")}
                  </label>
                  <button
                    ref={(el) => {
                      subgroupDropdownRefs.current[subgroup.id] = {
                        ...(subgroupDropdownRefs.current[subgroup.id] || {}),
                        toggle: el,
                      };
                    }}
                    onClick={() => openSubgroupDropdown(subgroup.id)}
                    className="w-full flex items-center justify-between border border-gray-300 rounded-md h-12 px-4 text-lg bg-white focus:outline-none"
                  >
                    <span>
                      {subgroup.selectedProducts.length > 0
                        ? `${subgroup.selectedProducts.length} ${t("selected")}`
                        : t("Select products")}
                    </span>
                    <DownOutlined className={`transition-transform ${subgroup.showDropdown ? "rotate-180" : ""}`} />
                  </button>

                  {subgroup.showDropdown && (
                    <div
                      ref={(el) => {
                        subgroupDropdownRefs.current[subgroup.id] = {
                          ...(subgroupDropdownRefs.current[subgroup.id] || {}),
                          dropdown: el,
                        };
                      }}
                      className="absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg w-full mt-2 overflow-y-auto p-4 custom-scrollbar"
                      style={{ maxHeight: "300px" }}
                    >
                      <Input
                        prefix={<SearchOutlined />}
                        placeholder={t("Search products...")}
                        value={subgroup.searchQuery}
                        onChange={(e) => handleSubgroupSearchChange(subgroup.id, e.target.value)}
                        className="mb-3 text-lg h-12 rounded-md focus:outline-none"
                        suffix={
                          subgroup.searchQuery && (
                            <span
                              onClick={() => handleSubgroupSearchChange(subgroup.id, "")}
                              className="cursor-pointer text-gray-400 hover:text-gray-600 text-xl font-semibold"
                            >
                              ×
                            </span>
                          )
                        }
                      />

                      {subgroup.loading ? (
                        <div className="flex justify-center text-lg py-4">
                          <Spin />
                        </div>
                      ) : subgroup.products.length > 0 && subgroup.searchQuery.length > 1 ? (
                        subgroup.products.map((p) => (
                          <div
                            key={p.prod_id}
                            onClick={() => toggleSubgroupProduct(subgroup.id, p.prod_id)}
                            className={`flex items-center py-2 px-2 rounded-md cursor-pointer hover:bg-green-50 ${subgroup.selectedProducts.includes(p.prod_id) ? "bg-green-50" : ""
                              }`}
                          >
                            <Checkbox
                              checked={subgroup.selectedProducts.includes(p.prod_id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSubgroupProduct(subgroup.id, p.prod_id);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="ms-2 text-gray-800 text-lg">{p.name}</span>
                            </Checkbox>
                          </div>
                        ))
                      ) : (
                        subgroup.searchQuery.length > 1 && (
                          <p className="text-center text-gray-400 py-4">{t("No products found")}</p>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <Button
              type="dashed"
              block
              size="large"
              className="text-lg h-14 font-medium border-2 border-purple-600 text-purple-600"
              onClick={addSubgroup}
            >
              {t("+ Add Subgroup")}
            </Button>
          </div>
        )}

        {/* Flat product picker — only used when NOT creating subgroups */}
        {!subgroupSelected && (
          <div className={`mb-8 relative ${deviceType == "tablet" ? "w-1/2" : ""}`}>
            <label className="block text-xl mb-2 font-semibold text-gray-600">{t("Product List")}</label>

            <button
              ref={toggleBtnRef}
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-full flex items-center justify-between border border-gray-300 rounded-md h-12 px-4 text-lg focus:outline-none"
            >
              <span>
                {selectedProducts.length > 0
                  ? `${selectedProducts.length} ${t("selected")}`
                  : t("Select products")}
              </span>
              <DownOutlined className={`transition-transform ${showDropdown ? "rotate-180" : ""}`} />
            </button>

            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg w-full mt-2 overflow-y-auto p-4 custom-scrollbar"
                style={{ maxHeight: "300px" }}
              >
                <Input
                  prefix={<SearchOutlined />}
                  placeholder={t("Search products...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-3 text-lg h-12 rounded-md focus:outline-none"
                  suffix={
                    searchQuery && (
                      <span
                        onClick={() => setSearchQuery("")}
                        className="cursor-pointer text-gray-400 hover:text-gray-600 text-xl font-semibold"
                      >
                        ×
                      </span>
                    )
                  }
                />

                {loading ? (
                  <div className="flex justify-center text-lg py-4">
                    <Spin />
                  </div>
                ) : products.length > 0 && searchQuery.length > 1 ? (
                  products.map((p) => (
                    <div
                      key={p.prod_id}
                      onClick={() => toggleProduct(p.prod_id)}
                      className={`flex items-center py-2 px-2 rounded-md cursor-pointer hover:bg-green-50 ${selectedProducts.includes(p.prod_id) ? "bg-green-50" : ""
                        }`}
                    >
                      <Checkbox
                        checked={selectedProducts.includes(p.prod_id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleProduct(p.prod_id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="ms-2 text-gray-800 text-lg">{p.name}</span>
                      </Checkbox>
                    </div>
                  ))
                ) : (
                  searchQuery.length > 1 && (
                    <p className="text-center text-gray-400 py-4">{t("No products found")}</p>
                  )
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-4">
          <button
            onClick={handleClose}
            className="px-8 py-2 font-semibold text-lg border rounded-lg bg-white text-[#8DC63F] border-[#8DC63F]"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleSave}
            className="px-10 py-2 font-semibold text-lg border rounded-lg bg-[#8DC63F] text-white border-[#8DC63F]"
          >
            {t("Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewGroup_Modal;